import { PageLoading } from './utils/PageLoading';
import React, { lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  createRoutesFromElements,
  Route
} from 'react-router-dom';
import App from './App';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import PrivateLayout from './layouts/PrivateLayout';
import PublicLayout from './layouts/PublicLayout';
import AuthLayout from './layouts/AuthLayout';
import MarkdownEditor from './components/MarkdownEditor/MarkdownEditor';

// Import new pages
import SignupPage from './pages/SignupPage';
import InviteAcceptancePage from './pages/InviteAcceptancePage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';

// Lazy load pages
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const SerialListPage = lazy(() => import('./pages/SerialListPage'));
const SerialDetailPage = lazy(() => import('./pages/SerialDetailPage'));
const ProjectDetailPage = lazy(() => import('./pages/ProjectDetailPage'));
const CreatePagePage = lazy(() => import('./pages/CreatePagePage'));
const PageEditorPage = lazy(() => import('./pages/PageEditorPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// New pages
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const TimelinePage = lazy(() => import('./pages/TimelinePage'));
const CharacterLibraryPage = lazy(() => import('./pages/CharacterLibraryPage'));
const CharacterProfilePage = lazy(() => import('./pages/CharacterProfilePage'));
const NotesPage = lazy(() => import('./pages/NotesPage'));
const PaletteDesignerPage = lazy(() => import('./pages/PaletteDesignerPage'));
const PaletteExtractorPage = lazy(() => import('./pages/PaletteExtractorPage'));
const FountainEditorPage = lazy(() => import('./pages/FountainEditorPage'));
const PagePromptsPage = lazy(() => import('./pages/PagePromptsPage'));
const FountainConverterPage = lazy(() => import('./pages/FountainConverterPage'));
const ScriptAnalyzerPage = lazy(() => import('./pages/ScriptAnalyzerPage'));
const ScriptEditorPage = lazy(() => import('./pages/ScriptEditorPage'));

// Admin pages
const AdminOverviewPage = lazy(() =>
  import('./pages/AdminPage').then((m) => ({ default: m.AdminOverviewPage }))
);
const AdminUsersPage = lazy(() =>
  import('./pages/AdminPage').then((m) => ({ default: m.AdminUsersPage }))
);
const AdminPlansPage = lazy(() =>
  import('./pages/AdminPage').then((m) => ({ default: m.AdminPlansPage }))
);
const AdminLogsPage = lazy(() =>
  import('./pages/AdminPage').then((m) => ({ default: m.AdminLogsPage }))
);

// Auth callback route
const AuthCallbackPage = lazy(() => import('./pages/AuthCallbackPage'));

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<AuthLayout />}>
      <Route element={<PublicLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route
          path="/auth/callback/:provider"
          element={
            <Suspense fallback={<PageLoading />}>
              <AuthCallbackPage />
            </Suspense>
          }
        />
        <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
      </Route>

      {/*
        Palette extractor is a public page accessible to all users.
        Placed outside PublicLayout so logged-in users aren't redirected.
      */}
      <Route
        path="/palette-extractor"
        element={
          <Suspense fallback={<PageLoading />}>
            <PaletteExtractorPage />
          </Suspense>
        }
      />

      {/*
        Free Fountain editor — no auth required, no API calls.
        Uses localStorage for persistence.
      */}
      <Route
        path="/fountain-editor"
        element={
          <Suspense fallback={<PageLoading />}>
            <FountainEditorPage />
          </Suspense>
        }
      />

      {/*
        Free one-page drawing prompts + blocking editor — no auth required,
        no API calls. Prompts and panel blocking state live in localStorage.
      */}
      <Route
        path="/page-prompts"
        element={
          <Suspense fallback={<PageLoading />}>
            <PagePromptsPage />
          </Suspense>
        }
      />

      {/*
        Free Fountain → Markdown converter — no auth required, no API calls.
        Conversion and input state are entirely client-side (localStorage).
      */}
      <Route
        path="/fountain-converter"
        element={
          <Suspense fallback={<PageLoading />}>
            <FountainConverterPage />
          </Suspense>
        }
      />

      {/*
        Free script statistics analyzer — dialogue/action balance, scene and
        panel counts, page count, character presence. No auth, no API calls.
      */}
      <Route
        path="/script-analyzer"
        element={
          <Suspense fallback={<PageLoading />}>
            <ScriptAnalyzerPage />
          </Suspense>
        }
      />

      {/*
        Invite acceptance is outside PublicLayout/PrivateLayout so both
        logged-in and logged-out users can reach it. The component handles
        the different states internally.
      */}
      <Route path="/accept-invite" element={<InviteAcceptancePage />} />

      <Route path="/home" element={<PrivateLayout />}>
        <Route path="/home" element={<DashboardPage />} />
        <Route
          path="/home/editor"
          element={
            <Suspense fallback={<PageLoading />}>
              <div>
                <MarkdownEditor />
              </div>
            </Suspense>
          }
        />
        <Route
          path="/home/serials"
          element={
            <Suspense fallback={<PageLoading />}>
              <SerialListPage />
            </Suspense>
          }
        />
        <Route
          path="/home/serials/:serialId"
          element={
            <Suspense fallback={<PageLoading />}>
              <SerialDetailPage />
            </Suspense>
          }
        />
        <Route
          path="/home/serials/:serialId/characters"
          element={
            <Suspense fallback={<PageLoading />}>
              <CharacterLibraryPage />
            </Suspense>
          }
        />
        <Route
          path="/home/serials/:serialId/characters/:characterId"
          element={
            <Suspense fallback={<PageLoading />}>
              <CharacterProfilePage />
            </Suspense>
          }
        />
        <Route
          path="/home/serials/:serialId/projects/:projectId"
          element={
            <Suspense fallback={<PageLoading />}>
              <ProjectDetailPage />
            </Suspense>
          }
        />
        <Route
          path="/home/serials/:serialId/projects/:projectId/pages/new"
          element={
            <Suspense fallback={<PageLoading />}>
              <CreatePagePage />
            </Suspense>
          }
        />
        <Route
          path="/home/serials/:serialId/projects/:projectId/script-editor"
          element={
            <Suspense fallback={<PageLoading />}>
              <ScriptEditorPage />
            </Suspense>
          }
        />
        <Route
          path="/home/serials/:serialId/projects/:projectId/pages/:pageId/edit"
          element={
            <Suspense fallback={<PageLoading />}>
              <PageEditorPage />
            </Suspense>
          }
        />
        <Route
          path="/home/analytics"
          element={
            <Suspense fallback={<PageLoading />}>
              <AnalyticsPage />
            </Suspense>
          }
        />
        <Route
          path="/home/timeline"
          element={
            <Suspense fallback={<PageLoading />}>
              <TimelinePage />
            </Suspense>
          }
        />
        <Route
          path="/home/notes"
          element={
            <Suspense fallback={<PageLoading />}>
              <NotesPage />
            </Suspense>
          }
        />
        <Route
          path="/home/palette"
          element={
            <Suspense fallback={<PageLoading />}>
              <PaletteDesignerPage />
            </Suspense>
          }
        />
        <Route
          path="/home/palette/:paletteId"
          element={
            <Suspense fallback={<PageLoading />}>
              <PaletteDesignerPage />
            </Suspense>
          }
        />
        <Route
          path="/home/palette/starter/:starterId"
          element={
            <Suspense fallback={<PageLoading />}>
              <PaletteDesignerPage />
            </Suspense>
          }
        />
        {/* Admin Routes */}
        <Route
          path="/home/admin"
          element={
            <Suspense fallback={<PageLoading />}>
              <AdminOverviewPage />
            </Suspense>
          }
        />
        <Route
          path="/home/admin/users"
          element={
            <Suspense fallback={<PageLoading />}>
              <AdminUsersPage />
            </Suspense>
          }
        />
        <Route
          path="/home/admin/plans"
          element={
            <Suspense fallback={<PageLoading />}>
              <AdminPlansPage />
            </Suspense>
          }
        />
        <Route
          path="/home/admin/logs"
          element={
            <Suspense fallback={<PageLoading />}>
              <AdminLogsPage />
            </Suspense>
          }
        />
        <Route
          path="/home/settings"
          element={
            <Suspense fallback={<PageLoading />}>
              <SettingsPage />
            </Suspense>
          }
        >
          <Route
            path="account"
            element={
              <Suspense fallback={<PageLoading />}>
                <SettingsPage />
              </Suspense>
            }
          />
          <Route
            path="billing"
            element={
              <Suspense fallback={<PageLoading />}>
                <SettingsPage />
              </Suspense>
            }
          />
          <Route
            path="integrations"
            element={
              <Suspense fallback={<PageLoading />}>
                <SettingsPage />
              </Suspense>
            }
          />
          <Route
            path="groups"
            element={
              <Suspense fallback={<PageLoading />}>
                <SettingsPage />
              </Suspense>
            }
          />
          <Route
            path="groups/:groupId"
            element={
              <Suspense fallback={<PageLoading />}>
                <SettingsPage />
              </Suspense>
            }
          />
        </Route>
        <Route
          path="/home/not-found"
          element={
            <Suspense fallback={<PageLoading />}>
              <NotFoundPage />
            </Suspense>
          }
        />
      </Route>
    </Route>
  ),
  { basename: `${import.meta.env.BASE_URL}` }
);

const Router = () => {
  return <RouterProvider router={router} />;
};

export default Router;
